"use client";

import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
interface HeaderProps {
  menuId?: string;
  logoSrc?: string;
  logoAlt?: string;
  showBookingButton?: boolean;
  bookingButtonText?: string;
  bookingButtonLink?: string;
}
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <div className="flex gap-4 md:gap-6">
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
        <div className="h-5 w-16 bg-muted/20 animate-pulse rounded" />
      </div>;
  }
  return <>
      <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden hover:!bg-transparent focus:!bg-transparent min-h-[44px] min-w-[44px]" aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      <NavigationMenu viewport={false} className="hidden lg:flex">
        <NavigationMenuList className="gap-4 lg:gap-6 xl:gap-8">
          {menuItems.slice(0, 4).map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="relative text-sm md:text-base font-medium text-foreground transition-colors hover:text-foreground hover:!bg-transparent focus:!bg-transparent after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      {mobileMenuOpen && <div className="lg:hidden absolute top-full left-0 right-0 bg-background border-b shadow-md">
          <nav className="container mx-auto px-6 py-4">
            <ul className="flex flex-col gap-3">
              {menuItems.slice(0, 4).map((item, i) => <li key={item.id} data-index={i}>
                  <Link to={item.href || "#"} className="block py-3 text-base font-medium text-foreground hover:text-foreground transition-colors min-h-[44px] flex items-center" onClick={() => setMobileMenuOpen(false)}>
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
        </div>}
    </>;
}
export default function Header({
  menuId = "3",
  logoSrc = "https://rational-stag.10web.cloud/wp-content/uploads/2026/01/SBA-pdf_page-0001.jpg",
  logoAlt = "Salem Ballama Auditing",
  showBookingButton = true,
  bookingButtonText = "Book Consultation",
  bookingButtonLink = "/BookConsultationPage"
}: HeaderProps = {}) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-background border-b transition-all duration-300 ${scrolled ? "h-20 md:h-24 lg:h-28" : "h-24 md:h-28 lg:h-32"} shadow-md`}>
      <div className="container mx-auto px-6 md:px-8 lg:px-12 h-full flex items-center gap-4 md:gap-6 lg:gap-8 !rounded-3xl !text-[hsl(22,78%,26%)] !justify-center !border-none border-[rgb(118,53,15)]">
        <Link to="/" className="flex-shrink-0 hover:opacity-90 transition-opacity">
          <img src="https://rational-stag.10web.cloud/wp-content/uploads/2026/01/logo-1.png" alt={logoAlt} className="h-16 !text-[hsl(22,78%,26%)] md:h-20 lg:h-24 xl:h-28" />
        </Link>

        <div className="flex items-center gap-3 md:gap-4 lg:gap-6 ml-auto">
          <MenuProvider menu_id={menuId} className="flex items-center">
            <HeaderMenuConsumer />
          </MenuProvider>

          <div className="hidden lg:flex items-center gap-3 xl:gap-4">
            {showBookingButton && <Button asChild size="sm" className="bg-primary text-foreground hover:bg-primary-hover hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-primary/30 active:translate-y-0 transition-all duration-150 hover:!bg-primary-hover focus:!bg-primary !bg-[hsl(22,78%,26%)] text-sm md:text-base">
                <Link to={bookingButtonLink} className="inline-flex items-center justify-center whitespace-nowrap text-sm md:text-base font-medium [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive shadow-xs h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 bg-primary transition-all duration-150 !bg-[hsl(22,78%,26%)] !text-[hsl(0,0%,100%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary-hover hover:-translate-y-0.5 hover:shadow-md hover:ring-2 hover:ring-primary/30 active:translate-y-0 hover:!bg-primary-hover focus:!bg-primary">
                  {bookingButtonText}
                </Link>
              </Button>}
          </div>
        </div>
      </div>
    </header>;
}